/**
 * US4/T021-T023 (FR-013, FR-028 to FR-033, DA-07). Preview + commit step of the two-route flow
 * `List.tsx` starts. There is no `GET /stock-uploads/{id}` anywhere in Warehouse (verified against
 * `InventoryController.cs`), and this app's `eslint.config.mjs` bans `sessionStorage`/`localStorage`
 * outright (FR-005) — so `List.tsx` caches the `UploadValidation` only in memory
 * (`upload-cache.ts`), for this page load, before navigating here; this screen overwrites the same
 * entry with the `UploadCommit` result once committed. **A hard refresh loses it** — a disclosed
 * divergence from tasks.md T021's "the id in the route so a refresh survives" (see
 * `upload-cache.ts`'s own note), not a silent gap: the empty-cache branch below names exactly why
 * and links back to re-upload, rather than a generic 404.
 *
 * The errors table follows the same `LegacyTable` (`@core/components/legacy-table`) embedded-table
 * pattern `invoicing/Detail.tsx` uses for an already-fetched, bounded array, copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-details.tsx — not `PagedTable`'s
 * server-pagination machinery, which has nothing to page against here.
 */
import { useState } from "react";
import { Link, replace, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import { ApiError } from "../../../lib/api";
import { isGuid, UPLOAD_PREVIEW_PATH } from "../../../lib/paths";
import toast from "react-hot-toast";
import { PiArrowLeftBold as ArrowLeft } from "react-icons/pi";
import { Alert, Button, Text } from "rizzui";
import { PageHeader, WidgetCard, EmptyState, ErrorSurface } from "@steelyard/theme";
import LegacyTable, { HeaderCell } from "@core/components/legacy-table";
import { can } from "../../../lib/permissions";
import { formatMoment } from "../../../lib/format";
import { BackLink } from "../../../lib/navigation";
import type { AccountSummary, PermissionState } from "../../../types";
import {
  commitStockUpload,
  uploadErrorReason,
  type UploadCommit,
  type UploadError,
  type UploadValidation,
} from "../../../lib/services/warehouse";
import { cacheUpload, getCachedUpload } from "./upload-cache";

/** `/inventory/uploads/:uploadId`, the address before the preview had its own: sent to the preview. */
export function legacyUploadLoader({ params }: LoaderFunctionArgs) {
  if (!isGuid(params.uploadId)) throw new ApiError(404, { title: "Not Found", status: 404 });
  return replace(UPLOAD_PREVIEW_PATH);
}

function isCommit(value: UploadValidation | UploadCommit): value is UploadCommit {
  return "rowsApplied" in value;
}

const errorColumns = [
  {
    title: <HeaderCell title="Row" align="right" />,
    dataIndex: "row",
    key: "row",
    render: (value: UploadError["row"]) => <span className="block text-right font-mono">{value}</span>,
  },
  {
    title: "Reason",
    dataIndex: "reason",
    key: "reason",
    render: (value: UploadError["reason"]) => uploadErrorReason(value),
  },
  {
    title: "Value",
    dataIndex: "value",
    key: "value",
    render: (value: UploadError["value"]) => <span className="font-mono">{value}</span>,
  },
];

export default function StockUploadPreview() {
  const { account, permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [cached, setCached] = useState(getCachedUpload);
  const [error, setError] = useState<unknown>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!can(permissionState, "inventory.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="commit a stock upload" />;
  }

  const header = (
    <PageHeader
      title="Bulk Stock Upload"
      breadcrumb={[{ name: "Home", href: "/" }, { name: "Stock", href: "/inventory" }, { name: "Bulk Upload" }]}
    >
      <BackLink to="/inventory">
        <Button as="span" variant="text">
          <ArrowLeft aria-hidden />
          Back to stock
        </Button>
      </BackLink>
    </PageHeader>
  );

  if (!cached) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        {header}
        <EmptyState
          title="This preview isn't available anymore"
          action={
            <Link to="/inventory/uploads">
              <Button as="span" size="sm">
                Upload a file
              </Button>
            </Link>
          }
        >
          The preview lives only in this page&apos;s memory for this browser tab — a refresh, a
          different tab, or opening this link directly all lose it, because Warehouse keeps no{" "}
          <code>GET</code> route for a staged upload to re-fetch from. Choose the file again to see
          its preview.
        </EmptyState>
      </div>
    );
  }

  const commit = isCommit(cached) ? cached : null;
  const validation = isCommit(cached) ? null : cached;

  async function onCommit() {
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await commitStockUpload(validation!.uploadId);
      cacheUpload(result);
      setCached(result);
      toast.success("Upload committed");
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      {header}

      {error ? <ErrorSurface error={error} action="commit this upload" /> : null}

      {commit ? (
        <Alert color="success" bar>
          <p className="font-semibold">Committed.</p>
          <p>
            {/* The committer is always the account submitting this form — shown by name/email
                rather than the server's raw account id. */}
            {commit.rowsApplied} row(s) applied, by {account.displayName ?? account.email ?? "you"}, at{" "}
            {formatMoment(commit.committedAt)}.
          </p>
        </Alert>
      ) : null}

      {validation ? (
        <>
          {validation.alreadyCommittedAt ? (
            <Alert color="warning" bar>
              This exact file was already committed, at {formatMoment(validation.alreadyCommittedAt)}.
              Committing again is refused — a deliberate re-application is an adjustment with a
              reason, not a repeat commit.
            </Alert>
          ) : null}

          {/* FR-013/T022: additions, changes and errors, separately and labelled — never one merged count. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <WidgetCard title="Additions">
              <Text className="text-3xl font-bold">{validation.additions}</Text>
              <Text className="text-sm text-muted-foreground">New SKUs this warehouse holds no stock row for yet.</Text>
            </WidgetCard>
            <WidgetCard title="Changes">
              <Text className="text-3xl font-bold">{validation.changes}</Text>
              <Text className="text-sm text-muted-foreground">SKUs this warehouse already stocks.</Text>
            </WidgetCard>
            <WidgetCard title="Errors">
              <Text className="text-3xl font-bold">{validation.errors.length}</Text>
              <Text className="text-sm text-muted-foreground">Rows that will not be applied.</Text>
            </WidgetCard>
          </div>

          {validation.errors.length > 0 ? (
            <WidgetCard title="Rejected rows">
              <LegacyTable
                data={validation.errors}
                caption={<span className="sr-only">Rows rejected by validation, with row number and reason</span>}
                columns={errorColumns}
                variant="modern"
                rowKey={(_record, index) => index ?? 0}
              />
            </WidgetCard>
          ) : null}

          <div>
            <Button
              onClick={onCommit}
              isLoading={isSubmitting}
              disabled={isSubmitting || !!validation.alreadyCommittedAt || (validation.additions === 0 && validation.changes === 0)}
            >
              {isSubmitting ? "Committing…" : "Commit"}
            </Button>
            {!validation.alreadyCommittedAt && validation.additions === 0 && validation.changes === 0 ? (
              <Text className="text-muted-foreground mt-2 text-sm">
                {validation.errors.length === 0
                  ? "Nothing to commit — the file had no data rows. Upload a CSV with a skuCode,quantity header and at least one row."
                  : "Nothing to commit — every row failed validation."}
              </Text>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
